'use client';

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';

interface Section {
    id: string;
    label: string;
}

interface StickyNavigationProps {
    sections: Section[];
}

export default function StickyNavigation({ sections }: StickyNavigationProps) {
    const [activeSection, setActiveSection] = useState('');
    const [isVisible, setIsVisible] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const mobileRef = useRef<HTMLDivElement>(null);
    const menuItemRefs = useRef<Array<HTMLButtonElement | null>>([]);

    // Resize the ref array when sections change so stale entries from a prior
    // (longer) sections list don't keep holding onto detached DOM nodes.
    useEffect(() => {
        menuItemRefs.current.length = sections.length;
    }, [sections.length]);

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            setIsVisible(scrollY > 300);

            const sectionElements = sections.map(section => ({
                id: section.id,
                element: document.getElementById(section.id),
            })).filter(item => item.element !== null);

            let foundActiveSection = sections[0]?.id || '';

            let maxVisibleHeight = 0;
            for (const { id, element } of sectionElements) {
                if (element) {
                    const rect = element.getBoundingClientRect();
                    const visibleTop = Math.max(0, 140 - rect.top);
                    const visibleBottom = Math.min(rect.height, window.innerHeight - rect.top);
                    const visibleHeight = Math.max(0, visibleBottom - visibleTop);

                    if (visibleHeight > maxVisibleHeight && rect.top < window.innerHeight * 0.6) {
                        maxVisibleHeight = visibleHeight;
                        foundActiveSection = id;
                    }
                }
            }

            setActiveSection(foundActiveSection);
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [sections]);

    // When the mobile menu opens, focus the active item (or the first) so
    // keyboard users can immediately navigate with Arrow/Home/End.
    useEffect(() => {
        if (!isMobileOpen) return;
        const activeIndex = Math.max(
            0,
            sections.findIndex((s) => s.id === activeSection)
        );
        requestAnimationFrame(() => menuItemRefs.current[activeIndex]?.focus());
    }, [isMobileOpen, activeSection, sections]);

    // Close mobile menu on outside click or Escape.
    useEffect(() => {
        if (!isMobileOpen) return;
        const onDown = (e: MouseEvent) => {
            if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
                setIsMobileOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsMobileOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [isMobileOpen]);

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const headerHeight = 80;
            const elementPosition = element.offsetTop - headerHeight;
            window.scrollTo({ top: elementPosition, behavior: 'smooth' });
        }
        setIsMobileOpen(false);
    };

    if (!isVisible) return null;

    const activeLabel =
        sections.find((s) => s.id === activeSection)?.label ?? sections[0]?.label ?? '';

    const moveFocus = (target: number) => {
        const clamped = (target + sections.length) % sections.length;
        menuItemRefs.current[clamped]?.focus();
    };

    return (
        // pointer-events-none on the outer wrapper keeps the transparent gutters
        // around the pill/menu from blocking clicks to the page content beneath;
        // each interactive child (the mobile button, the desktop pill, the menu)
        // opts back in with pointer-events-auto so it stays clickable.
        <div className="fixed top-24 sm:top-24 inset-x-0 z-40 flex justify-center px-3 sm:px-4 pointer-events-none">
            {/* Mobile: compact dropdown showing current section, expands to a menu. */}
            <div ref={mobileRef} className="sm:hidden pointer-events-auto w-full max-w-sm relative">
                <motion.button
                    type="button"
                    onClick={() => setIsMobileOpen((v) => !v)}
                    aria-expanded={isMobileOpen}
                    aria-haspopup="menu"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="w-full flex items-center justify-between gap-3 min-h-11 px-4 py-2 rounded-full bg-white/95 dark:bg-slate-800/95 backdrop-blur-md shadow-xl border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                    <span className="truncate">{activeLabel}</span>
                    <ChevronDown
                        className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isMobileOpen ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                    />
                </motion.button>
                <AnimatePresence>
                    {isMobileOpen && (
                        <motion.ul
                            role="menu"
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.18 }}
                            onKeyDown={(e) => {
                                const idx = menuItemRefs.current.findIndex(
                                    (b) => b === document.activeElement
                                );
                                if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    moveFocus(idx < 0 ? 0 : idx + 1);
                                } else if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    moveFocus(idx < 0 ? sections.length - 1 : idx - 1);
                                } else if (e.key === 'Home') {
                                    e.preventDefault();
                                    moveFocus(0);
                                } else if (e.key === 'End') {
                                    e.preventDefault();
                                    moveFocus(sections.length - 1);
                                }
                            }}
                            className="absolute top-full left-0 right-0 mt-2 rounded-2xl bg-white dark:bg-slate-800 shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden max-h-[70vh] overflow-y-auto"
                        >
                            {sections.map((section, i) => {
                                const isActive = activeSection === section.id;
                                return (
                                    <li key={section.id} role="none">
                                        <button
                                            type="button"
                                            role="menuitem"
                                            ref={(el) => { menuItemRefs.current[i] = el; }}
                                            tabIndex={isActive ? 0 : -1}
                                            onClick={() => scrollToSection(section.id)}
                                            className={`w-full flex items-center justify-between gap-3 min-h-11 px-4 py-3 text-left text-sm font-medium transition-colors ${isActive
                                                ? 'bg-gold/10 text-gold'
                                                : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'
                                                }`}
                                        >
                                            <span className="truncate">{section.label}</span>
                                            {isActive && (
                                                <Check className="w-4 h-4 shrink-0 text-gold" aria-hidden="true" />
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </motion.ul>
                    )}
                </AnimatePresence>
            </div>

            {/* Desktop: full pill strip with all sections visible. */}
            <motion.nav
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="hidden sm:block pointer-events-auto bg-white/95 dark:bg-slate-800/95 backdrop-blur-md rounded-full shadow-xl border border-slate-200 dark:border-slate-700 max-w-full"
            >
                <div className="flex items-center gap-1 p-2">
                    {sections.map((section) => (
                        <button
                            key={section.id}
                            onClick={() => scrollToSection(section.id)}
                            className={`shrink-0 min-h-11 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${activeSection === section.id
                                ? 'bg-gold text-white shadow-md'
                                : 'text-slate-600 dark:text-slate-300 hover:text-gold dark:hover:text-gold hover:bg-gold/10 dark:hover:bg-gold/10'
                                }`}
                        >
                            {section.label}
                        </button>
                    ))}
                </div>
            </motion.nav>
        </div>
    );
}
